// Free Security Risk Assessment — 10 questions, 0-3 points each, max 30.
const q = (id, prompt, options) => ({ id, prompt, options });
const opt = (label, value) => ({ label, value });

export const riskAssessmentQuestions = [
  q('scanning', 'How often do you run vulnerability scans across your external-facing systems?', [
    opt('Never', 0),
    opt('Once a year or less', 1),
    opt('Quarterly', 2),
    opt('Continuously, or at least monthly', 3),
  ]),
  q('mfa', 'Is multi-factor authentication (MFA) enforced for admin and privileged accounts?', [
    opt('No MFA anywhere', 0),
    opt('MFA on some accounts', 1),
    opt('MFA on all privileged accounts', 2),
    opt('MFA enforced organization-wide, including SSO', 3),
  ]),
  q('pentest', 'When was your last third-party penetration test?', [
    opt('Never, or more than 2 years ago', 0),
    opt('1–2 years ago', 1),
    opt('Within the last year', 2),
    opt('Within the last 6 months, with a retest', 3),
  ]),
  q('ir-plan', 'Do you have a documented, tested incident response plan?', [
    opt('No plan exists', 0),
    opt('Plan exists but has never been tested', 1),
    opt('Tested once via a tabletop exercise', 2),
    opt('Tested and refined at least annually', 3),
  ]),
  q('backups', 'How confident are you that your backups would actually restore during a ransomware incident?', [
    opt('Not confident — restores have never been tested', 0),
    opt('Backups exist, but restores are untested', 1),
    opt('Restores are tested occasionally', 2),
    opt('Restores tested quarterly, with an offline or immutable copy', 3),
  ]),
  q('monitoring', 'Do you have continuous security monitoring (SOC/SIEM) covering your environment?', [
    opt('No monitoring in place', 0),
    opt('Logs are collected but not actively reviewed', 1),
    opt('Business-hours monitoring', 2),
    opt('24/7 monitoring with analyst triage', 3),
  ]),
  q('compliance', 'Are you currently certified or working toward a recognized security framework (ISO 27001, SOC 2, PCI DSS)?', [
    opt('Not aligned to any framework', 0),
    opt('Working toward one informally', 1),
    opt('Formally in progress', 2),
    opt('Certified or under active audit', 3),
  ]),
  q('awareness', 'How would you rate security awareness training across your non-technical staff?', [
    opt('No training program', 0),
    opt('One-time onboarding only', 1),
    opt('Annual training', 2),
    opt('Ongoing training with phishing simulations', 3),
  ]),
  q('inventory', 'Do you have a current, accurate inventory of internet-facing assets and third-party integrations?', [
    opt('No inventory', 0),
    opt('Partial or outdated', 1),
    opt('Maintained, but manually', 2),
    opt('Maintained with automated discovery', 3),
  ]),
  q('ownership', 'Is there a named individual accountable for security decisions?', [
    opt('No one specifically', 0),
    opt('An IT lead handles it alongside other duties', 1),
    opt('A dedicated security lead exists', 2),
    opt('A vCISO or CISO reports to the board', 3),
  ]),
];

export const riskAssessmentMeta = {
  eyebrow: 'Free Tool',
  heroTitle: 'Free Security Risk Assessment',
  heroDesc: 'Ten questions, about three minutes. No email required to see your result — this scores your current exposure across the fundamentals we check first in every real engagement.',
  resultsIntro: 'Your Risk Profile',
};

export const computeRiskResult = (answers, questions) => {
  const maxScore = questions.reduce((sum, ques) => sum + Math.max(...ques.options.map((o) => o.value)), 0);
  const score = questions.reduce((sum, ques) => sum + (answers[ques.id] ?? 0), 0);
  const pct = Math.round((score / maxScore) * 100);

  let tier, tierDesc, ctaHref, ctaLabel;
  if (pct < 40) {
    tier = 'High Exposure';
    tierDesc = 'You are missing enough fundamentals that a motivated attacker would likely succeed. This is not a judgment — most organizations at this stage simply have not had the time or budget to build these controls yet. The good news is that the highest-impact fixes (MFA, tested backups, a baseline pentest) are also the fastest to put in place.';
    ctaHref = '/services/vapt';
    ctaLabel = 'Start With a Baseline Penetration Test';
  } else if (pct < 75) {
    tier = 'Developing';
    tierDesc = 'You have real controls in place, but there are gaps an experienced tester or a determined attacker would still find — usually in testing cadence, monitoring coverage, or incident response readiness. This is the most common place we find mid-market organizations, and it is a good position to formalize a security program from.';
    ctaHref = '/services/cyber-security-strategic-consulting';
    ctaLabel = 'Get a Prioritized Roadmap';
  } else {
    tier = 'Resilient';
    tierDesc = 'Your fundamentals are solid. At this stage, the highest-value work shifts from "closing gaps" to continuous validation — regular testing, tuned detection, and making sure the controls you have built still work as your environment changes.';
    ctaHref = '/services/security-operations-centre';
    ctaLabel = 'Explore Continuous Monitoring';
  }

  return { score, maxScore, pct, tier, tierDesc, ctaHref, ctaLabel };
};
